import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import { Element, scroller } from 'react-scroll';
import {
  find, first, map, indexOf, values, reduce, filter,
  without, concat, each, defer, camelCase, findIndex,
} from 'lodash';
import numeral from 'numeral';
import Fa from 'react-fontawesome';
import { Dropdown, MenuItem, OverlayTrigger, Tooltip, ProgressBar } from 'react-bootstrap';
import ProgressBar2 from '../../../../components/progress-bar';
import { getColorFromKey } from '../../../../util/helpers';
import styles from './styles.css';
import chartStyles from '../brenchmark-graph/chart-styles.css';
import MoneyDisplay, { format as formatMoney } from '../../../../components/money-display';
import NumberDisplay from '../../../../components/number-display';
import OwnerSelector from '../../../../components/owner-selector';
import CircleGraph from '../../../../components/circle-graph';
import WindowResizeListener from '../../../../components/window-size-listener';

import {
  getConversionGraphOpportunities,
  getConversionGraphLoading,
  getConversionGraphLoaded,
  getConversionGraphOwners,
  getConversionGraphAverage,
  getStages,
} from '../../PipelineReducer';

const typeOptions = [
  { label: 'Amount', value: 'amount' },
  { label: 'Count', value: 'count' },
];
const stageOptions = [
  { label: 'Win Rate', value: 'Won' },
  { label: 'Loss Rate', value: 'Lost' },
  { label: 'No Decision Rate', value: 'No Decision' },
];

const USER_PREFERENCE_REPORT_TYPE = 'pipeline-conversion-graph-report-type';

const rows = [
  { type: 'won', label: 'Conversion Rate', color: getColorFromKey('won') },
  { type: 'lost', label: 'Loss Rate', color: getColorFromKey('lost') },
  { type: 'noDecision', label: 'No Decision Rate', color: getColorFromKey('no decision') },
];

class ConversionGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reportType: 'amount',
      activeStages: map(stageOptions, 'value'),
      ownerId: null,
      containerWidth: 0,
    };
    this.setReportType = this.setReportType.bind(this);
    this.handleChartClick = this.handleChartClick.bind(this);
    this.handleSelectOwner = this.handleSelectOwner.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const reportType = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    this.setState({ reportType });
  }

  onWindowResize() {
    if (this.container) {
      const containerWidth = this.container.offsetWidth;
      this.setState({ containerWidth });
    }
  }

  setReportType(reportType) {
    this.setState({ reportType });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
  }

  handleChartClick(event) {
    this.props.onSelect(event.point.options.ids);
  }

  handleSelectOwner(owner) {
    this.setState({ ownerId: owner ? owner.user_id : null });
    defer(() => {
      this.onWindowResize();
    });
  }

  toggleStage(stage) {
    const { activeStages } = this.state;
    let newActiveStages = [...activeStages];

    if (indexOf(newActiveStages, stage) >= 0) {
      // remove
      newActiveStages = without(newActiveStages, stage);
    } else {
      newActiveStages = concat(newActiveStages, stage);
    }
    this.setState({ activeStages: newActiveStages });
    defer(() => {
      scroller.scrollTo('pipeline-conversion-chart', { duration: 500, smooth: true, offset: -70 });
    });
  }

  renderControls() {
    const { reportType } = this.state;
    const activeType = find(typeOptions, { value: reportType }) || first(typeOptions);

    return (
      <h5 className="sub-header-with-controls">
        Show&nbsp;
        <Dropdown
          id="type-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {activeType.label}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {typeOptions.map(s => (
              <MenuItem
                key={s.value}
                active={s.value === activeType.value}
                onSelect={() => { this.setReportType(s.value); }}
              >
                {s.label}
              </MenuItem>
              ))}
          </Dropdown.Menu>
        </Dropdown>
        &nbsp;conversion rates and compare with&nbsp;
        <OwnerSelector
          owners={this.props.owners}
          ownerId={this.state.ownerId}
          onSelect={this.handleSelectOwner}
        />
      </h5>
    );
  }

  renderConersionCircle(stageLabel, percent, comparePercent) {
    const { reportType } = this.state;
    let color = '#2196f3';
    if (comparePercent !== undefined) {
      // const comparePer = reportType === 'amount' ? compareStage.amountProbabilityRate : compareStage.countProbabilityRate;
      if (percent > comparePercent) {
        color = getColorFromKey('won');
      } else if (percent < comparePercent) {
        color = getColorFromKey('lost');
      }
    }
    const title = numeral(percent).format('0.1%');

    const tip = reportType === 'amount' ?
      (
        <div className={chartStyles.tooltip}>
          <div className="text-left">The amount you win based on trailing twelve month performance for opportunities in <strong>{stageLabel}</strong>.</div>
        </div>
      ) :
      (
        <div className={chartStyles.tooltip}>
          <div className="text-left">The percentage of opportunities you win based on trailing twelve month performance for opportunities in <strong>{stageLabel}</strong>.</div>
        </div>
      );


    return (
      <div className={styles.circle}>
        <CircleGraph
          title={title}
          value={percent}
          color={color}
          tooltip={tip}
          subTitle="Win Rate"
          half
        />
      </div>
    );
  }

  renderStageBars(stage, hasOwner, rowType) {
    const { reportType, containerWidth } = this.state;

    const val = stage[camelCase(`average ${rowType} ${reportType} Rate`)];
    const now = val * 100;
    const min = 30; // pixel
    const label = numeral(val).format('0.0%');
    let bsStyle = null;
    let footer = '';
    let prefix = rowType;
    let subTitle = rowType;
    if (rowType === 'won') {
      bsStyle = 'success';
      prefix = 'Converted';
      subTitle = 'Converted Rate';
      footer = (
        <span>
          * Conversion rate includes deals that advanced to a later stage as well as opportunities that closed as won while in this sales stage.
        </span>
      );
    } else if (rowType === 'lost') {
      bsStyle = 'danger';
      prefix = 'Lost';
      subTitle = 'Loss Rate';
      footer = (
        <span>
          * Loss rate includes deals that reached this sales stage as the furthest stage in their history.
        </span>
      );
    } else if (rowType === 'noDecision') {
      bsStyle = 'warning';
      prefix = 'No Decision';
      subTitle = 'No Decision Rate';
      footer = (
        <span>
          * No decision rate includes deals that reached this sales stage as the furthest stage in their history.
        </span>
      );
    }

    const hundred = (<MoneyDisplay value={100} abbreviated />);
    const winRate = (<MoneyDisplay value={stage[camelCase(`average ${reportType} Probability Rate`)] * 100} abbreviated />);

    const dealTypes = rowType === 'won' ? stage.convertedTypeStats : stage[camelCase(`${rowType} Type Stats`)];

    const dealTypeRows = [];
    each(dealTypes, (dealType, k) => {
      dealTypeRows.push((
        <tr key={camelCase(k)}>
          <td>
            {prefix} {k}
          </td>
          <td className="text-right">
            <NumberDisplay value={dealType.count} precision={0} />
          </td>
          <td className="text-right">
            <MoneyDisplay value={dealType.amount} abbreviated precision={1} />
          </td>
        </tr>));
    });
    const pop = (
      <Tooltip
        id={camelCase(`popover ${rowType} ${stage.label}`)}
        className={chartStyles.tooltip}
      >
        <div>
          <h1 className={chartStyles.tooltipTitle}>
            {subTitle}
          </h1>
          <h2 className={chartStyles.tooltipSubTitle}>
            Stage: {stage.label}
          </h2>
          <table className="table table-condensed">
            <tbody>
              {rows.map((r) => {
                const active = rowType === r.type;
                const style = {
                  fontWeight: active ? 'bold' : null,
                  color: active ? getColorFromKey(rowType) : null,
                };
                return (
                  <tr key={`${rowType}-${r.type}`}>
                    <td style={style}><nobr>{r.label}</nobr></td>
                    <td style={style} className="text-right">
                      {numeral(stage[camelCase(`average ${r.type} ${reportType} Rate`)]).format('0.0%')}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className="text-active">Stage Win Rate</td>
                <td className="text-active text-right">
                  <nobr>{winRate} of every {hundred}</nobr>
                </td>
              </tr>
            </tbody>
          </table>
          {dealTypeRows.length > 0 &&
            <table className="table table-condensed">
              <thead>
                <tr>
                  <th>&nbsp;</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {dealTypeRows}
              </tbody>
            </table>
          }
          <table className="table table-condensed">
            <tfoot>
              <tr>
                <td colSpan="3">
                  <em>{footer}</em>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Tooltip>
    );

    return (
      <OverlayTrigger trigger={['hover']} placement="top" overlay={pop}>
        <ProgressBar
          className={chartStyles.progress}
          now={now}
          label={val * containerWidth >= min ? label : null}
          bsStyle={bsStyle}
          onClick={() => {
            this.props.onSelect(rowType === 'won' ? stage.convertedIds : stage[camelCase(`${rowType} Ids`)]);
          }}
        />
      </OverlayTrigger>
    );
  }

  renderStageStack(stage, hasOwner, rowType) {
    const { reportType } = this.state;
    let stackValue = stage[camelCase(`${rowType} ${reportType}`)];
    if (rowType === 'won') {
      stackValue = stage[camelCase(`converted ${reportType}`)];
    }
    if (reportType === 'amount') {
      return (
        <nobr><MoneyDisplay value={stackValue} abbreviated precision={1} /></nobr>
      );
    }
    return (
      <nobr><NumberDisplay value={stackValue} precision={0} suffix="Deal" suffixIsUnit /></nobr>
    );
  }

  renderOwnerCell(stage, averageStage, rowType) {
    const { reportType } = this.state;
    const val = stage[camelCase(`${rowType} ${reportType} rate`)];
    const averageVal = averageStage[camelCase(`average ${rowType} ${reportType} rate`)];
    const gap = averageVal !== 0 ? (val - averageVal) / averageVal : 0;
    const plus = gap > 0 ? '+' : '';
    const row = find(rows, { type: rowType });
    let cls = null;
    if (rowType === 'won') {
      cls = val >= averageVal ? 'label-success' : 'label-danger';
    } else {
      cls = val >= averageVal ? 'label-danger' : 'label-success';
    }
    return [
      (
        <td
          key={camelCase(`${rowType}-${stage.value}-1`)}
          className={styles.ownerCell}
          width="50%"
        >
          <span><nobr>{row.label}</nobr></span>
        </td>
      ),
      (
        <td
          className={`${styles.ownerCell} text-right`}
          key={camelCase(`${rowType}-${stage.value}-2`)}
        >
          <NumberDisplay value={val * 100} precision={1} suffix="%" />
        </td>
      ),
      (
        <td
          className={`${styles.ownerCell} text-right`}
          key={camelCase(`${rowType}-${stage.value}-3`)}
        >
          <span className={`label ${cls}`}>{plus}<NumberDisplay value={gap * 100} precision={1} suffix="%" /></span>
        </td>
      ),
    ];
  }

  renderGraph() {
    const { allStages, owners } = this.props;
    const { ownerId, reportType } = this.state;
    const owner = ownerId ? find(owners, { user_id: ownerId }) : null;
    const hasOwner = owner !== null && owner !== undefined;

    const editedStages = reduce(allStages, (stages, stage, index) => {
      stages.push({ stage, index, rowType: 'won' }); // won
      stages.push({ stage, index, rowType: 'lost' }); // lost
      stages.push({ stage, index, rowType: 'noDecision' }); // no decision
      return stages;
    }, []);

    const earlyStages = filter(editedStages, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'early');
    const earlyStageIndex = findIndex(editedStages, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'early');
    if (earlyStageIndex >= 0) {
      editedStages.splice(earlyStageIndex, 0, { rowType: 'early', total: earlyStages.length });
    }
    const lateStages = filter(editedStages, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'late');
    const lateStageIndex = findIndex(editedStages, editedStage => editedStage.stage !== undefined && editedStage.stage.conversion_stage === 'late');
    if (lateStageIndex >= 0) {
      editedStages.splice(lateStageIndex, 0, { rowType: 'late', total: lateStages.length });
    }

    return (
      <div>
        <table className={`table table-condensed ${chartStyles.mainTable}`}>
          <thead>
            <tr>
              <th
                style={{ width: 10 }}
              >
                &nbsp;
              </th>
              <th
                colSpan="5"
                style={{ width: hasOwner ? '50%' : null }}
                className="text-center"
              >
                <span className={styles.mainHeader}>
                  Average Conversion Rates<br />
                  <small>
                    <em>for {numeral(owners.length).format('0')} {pluralize('sales representation', owners.length)}</em>
                  </small>
                </span>
              </th>
              {hasOwner &&
                <th
                  style={{ width: '50%', position: 'relative' }}
                  colSpan="4"
                  className="text-center"
                >
                  <span className={styles.vs}>
                    vs
                  </span>
                  <span className={styles.mainHeader}>
                    Conversion Rate<br />
                    <small>
                      <em>for {owner.display_name || owner.name}</em>
                    </small>
                  </span>
                </th>
              }
            </tr>
          </thead>
          <tbody>
            {hasOwner &&
              <tr>
                <td colSpan="6">&nbsp;</td>
                <td colSpan="2" className={`${styles.withBorder} ${styles.ownerCell}`}>&nbsp;</td>
                <td className={`text-right ${styles.ownerCell}`}><strong>Actual</strong></td>
                <td className={`text-right ${styles.ownerCell}`}><strong>Gap</strong></td>
              </tr>
            }
            {editedStages.map((editedStage, i) => {
              if (editedStage.rowType === 'early' || editedStage.rowType === 'late') {
                return [
                  (
                    <tr
                      className="text-left"
                      key={`${editedStage.rowType}-0`}
                      className={`${chartStyles.divider} ${chartStyles.separateRow}`}
                    >
                      <td colSpan="3">&nbsp;</td>
                      <td width={hasOwner ? '49%' : '99%'}>
                        {editedStage.rowType === 'early' &&
                          <div ref={(el) => { this.container = el; }}>
                            <WindowResizeListener onResize={this.onWindowResize} />
                          </div>
                        }
                      </td>
                      <td colSpan={hasOwner ? 6 : 2} />
                    </tr>
                  ),
                  (
                    <tr
                      key={`${editedStage.rowType}-1`}
                      className={`${chartStyles.divider} ${chartStyles.separateRow}`}
                    >
                      <td rowSpan={editedStage.total + 1} className={chartStyles.groupColumn}>
                        <span className={chartStyles.rotatedStage}>
                          <span className="label text-uppercase">
                            {editedStage.rowType} stages
                          </span>
                        </span>
                      </td>
                    </tr>
                  ),
                ];
              }
              const row = find(rows, { type: editedStage.rowType });
              const { stage } = editedStage;
              const ownerStage = hasOwner ? owner.stages[editedStage.index] : null;
              const isFirst = i === 0;
              const isFirstRow = editedStage.rowType === 'won';
              const isSkipped = editedStage.stage.skip_conversion;
              const cls = [];
              if (isSkipped) {
                cls.push(chartStyles.skippedRow);
              }
              if (isFirstRow) {
                cls.push(chartStyles.divider);
              }
              const skipTip = (
                <Tooltip
                  id={camelCase(`skipped ${editedStage.value}`)}
                  className={chartStyles.tooltip}
                >
                  <strong>{editedStage.stage.label}</strong> stage is excluded from all calculations.
                </Tooltip>
              );

              return (
                <tr key={`${editedStage.rowType}-${stage.value}`} className={cls.join(' ')}>
                  {isFirstRow && !isSkipped &&
                    <td
                      rowSpan="3"
                      style={{ verticalAlign: 'middle' }}
                    >
                      <span className={chartStyles.stageName}>{stage.label}</span>
                    </td>
                  }
                  {isFirstRow && isSkipped &&
                    <td
                      colSpan={hasOwner ? 9 : 5}
                    >
                      <OverlayTrigger trigger={['hover']} placement="top" overlay={skipTip}>
                        <span className={`${chartStyles.stageName} text-muted`}>{stage.label} <Fa name="exclamation-triangle" /></span>
                      </OverlayTrigger>
                    </td>
                  }
                  {!isSkipped &&
                    <td className="text-right">
                      <nobr>{row.label}</nobr>
                    </td>
                  }
                  {!isSkipped &&
                    <td className="text-right">
                      {this.renderStageBars(stage, hasOwner, editedStage.rowType)}
                    </td>
                  }
                  {!isSkipped &&
                    <td className="text-right">
                      {this.renderStageStack(stage, hasOwner, editedStage.rowType)}
                    </td>
                  }
                  {isFirstRow && !isSkipped &&
                    <td
                      className="text-right"
                      rowSpan="3"
                    >
                      {this.renderConersionCircle(stage.label, reportType === 'amount' ? stage.averageAmountProbabilityRate : stage.averageCountProbabilityRate)}
                    </td>
                  }
                  {hasOwner && isFirstRow && !isSkipped &&
                    <td
                      className={`${styles.ownerCell} ${styles.withBorder} text-center`}
                      rowSpan="3"
                    >
                      {this.renderConersionCircle(
                        ownerStage.label,
                        (reportType === 'amount' ? ownerStage.amountProbabilityRate : ownerStage.countProbabilityRate),
                        (reportType === 'amount' ? stage.averageAmountProbabilityRate : stage.averageCountProbabilityRate),
                      )}
                    </td>
                  }
                  {hasOwner && !isSkipped && this.renderOwnerCell(ownerStage, stage, editedStage.rowType, isFirst)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  renderStageToggle() {
    const { activeStages } = this.state;
    return (
      <div className={styles.legend}>
        <ul>
          {stageOptions.map((stage) => {
            const active = indexOf(activeStages, stage.value) >= 0;
            return (
              <li
                className={active ? styles.active : null}
                key={stage.value}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.toggleStage(stage.value);
                }}
              >
                <Fa name="circle" style={{ color: active ? getColorFromKey(stage.value) : null }} /> {stage.label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
  render() {
    const { loading, loaded } = this.props;
    return (
      <div className={styles.chart}>
        {(loading || !loaded) && <ProgressBar2 />}
        {!loading && loaded &&
          <div>
            {this.renderControls()}
            <Element name="pipeline-conversion-chart" />
            {this.renderGraph()}
          </div>
        }
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    stages: getStages(state),
    loaded: getConversionGraphLoaded(state),
    loading: getConversionGraphLoading(state),
    opportunities: getConversionGraphOpportunities(state),
    owners: values(getConversionGraphOwners(state)),
    allStages: getConversionGraphAverage(state),
  };
}

ConversionGraph.propTypes = {
  loaded: PropTypes.bool,
  loading: PropTypes.bool,
  onSelect: PropTypes.func,
  allStages: PropTypes.arrayOf(PropTypes.shape({})),
  owners: PropTypes.arrayOf(PropTypes.shape({})),
};

ConversionGraph.defaultProps = {
  loaded: false,
  loading: false,
  onSelect: () => {},
  owners: [],
  allStages: [],
};
ConversionGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.shape({}).isRequired,
  addNotification: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(ConversionGraph);
